import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faUser,
  faEnvelope,
  faSignInAlt,
  faBriefcase,
  faSignOutAlt,
  faBars,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../context/AuthContext";
import Loader from "./Loader";

const navItems = [
  { path: "/jobs", icon: faHome, label: "Jobs" },
  { path: "/post-job", icon: faBriefcase, label: "Post Job" },
  { path: "/profile", icon: faUser, label: "Profile" },
  { path: "/messages", icon: faEnvelope, label: "Messages" },
];

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleLogout = async () => {
    try {
      setIsLoading(true);
      // Add 4 second delay
      await new Promise(resolve => setTimeout(resolve, 4000));
      await logout();
      navigate("/");
    } finally {
      setIsLoading(false);
      setIsMenuOpen(false);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  if (!user) return null;

  return (
    <>
      {isLoading && <Loader type="logout" />}
      <nav className="nav dark:bg-gradient-to-r dark:from-gray-900 dark:to-gray-800 relative">
        <div className="nav-content flex justify-between items-center px-4 py-2">
          {/* Logo Section */}
          <div className="flex items-center">
            <Link to="/" className="nav-logo flex items-center">
              <img
                src="/favicon-logo.png"
                alt="RBU Logo"
                className="h-12 w-auto"
              />
              <span className="logo-text dark:text-white ml-4">
                RBU Alumni Portal
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-4">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="nav-item dark:hover:bg-gray-700"
              >
                <FontAwesomeIcon icon={item.icon} className="nav-icon" />
                <span>{item.label}</span>
              </Link>
            ))}
            <button
              onClick={handleLogout}
              className="nav-item nav-item-login dark:bg-blue-600 dark:hover:bg-blue-700"
            >
              <FontAwesomeIcon icon={faSignOutAlt} className="nav-icon" />
              <span>Logout</span>
            </button>
          </div>

          {/* Mobile Hamburger Button - Centered and Right Aligned */}
          <div className="lg:hidden flex items-center absolute right-4 top-1/2 -translate-y-1/2">
            <button
              onClick={toggleMenu}
              className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
              aria-label="Toggle menu"
            >
              <FontAwesomeIcon
                icon={isMenuOpen ? faTimes : faBars}
                className="h-6 w-6"
              />
            </button>
          </div>

          {/* Mobile Navigation Overlay */}
          <div
            className={`
          lg:hidden fixed inset-0 z-50 
          bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm
          transform transition-transform duration-300 ease-in-out
          ${isMenuOpen ? "translate-x-0" : "translate-x-full"}
        `}
          >
            {/* Close button in mobile menu */}
            <div className="p-4 flex justify-end">
              <button
                onClick={toggleMenu}
                className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
              >
                <FontAwesomeIcon icon={faTimes} className="h-6 w-6" />
              </button>
            </div>

            {/* Updated mobile menu layout */}
            <div className="flex flex-col h-[calc(100vh-80px)]">
              <div className="flex-1 overflow-y-auto px-4">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="mobile-nav-item"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <FontAwesomeIcon
                      icon={item.icon}
                      className="w-5 h-5 mr-4"
                    />
                    <span>{item.label}</span>
                  </Link>
                ))}
              </div>
              <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleLogout}
                  className="mobile-nav-item text-red-600 dark:text-red-400 w-full"
                >
                  <FontAwesomeIcon
                    icon={faSignOutAlt}
                    className="w-5 h-5 mr-4"
                  />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
